import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Nav, Footer } from "@/components/Nav";
import { ContabWorkspace } from "@/components/contabStats/ContabWorkspace";
import { EventScopeBar, useEventScope } from "@/components/EventScope";
import { eventDisplayName } from "@/lib/eventScope";
import {
    cbSoggetti, cbEventi, cbCreateEvento, cbCreateMovimento, cbDeleteMovimento,
    cbStripeRiepilogo, cbRiepilogo, photographerConfig,
} from "@/lib/api";

const TK_KEY = "velora_contab_token";
const OP_KEY = "velora_contab_operatore";
const TIPI_COSTO = ["DirettoEvento", "GeneraleInvestimento", "Manodopera"];
const COSTO_LABEL = {
    DirettoEvento: "Diretto evento",
    GeneraleInvestimento: "Generale / Investimento",
    Manodopera: "Manodopera",
};
const eur = (n) => `€ ${(Number(n) || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const today = () => new Date().toISOString().slice(0, 10);

export default function ContabilitaPage() {
    const { eventId } = useEventScope();
    const [token, setToken] = useState(localStorage.getItem(TK_KEY) || "");
    const [inputToken, setInputToken] = useState("");
    const doLogin = () => {
        if (!inputToken.trim()) return;
        localStorage.setItem(TK_KEY, inputToken.trim());
        setToken(inputToken.trim());
    };
    const doLogout = () => { localStorage.removeItem(TK_KEY); setToken(""); };

    if (!token) {
        return (
            <div className="min-h-screen bg-[#0B0D10] text-[#F4F2ED]">
                <Nav />
                <div className="max-w-md mx-auto px-5 py-16">
                    <h1 className="font-display text-3xl uppercase mb-2">Contabilità</h1>
                    <p className="text-xs text-[#9AA2AD] uppercase tracking-widest mb-6">Entrate / Uscite · movimenti</p>
                    <input
                        data-testid="contab-token-input"
                        type="password" value={inputToken} onChange={e => setInputToken(e.target.value)}
                        onKeyDown={e => e.key === "Enter" && doLogin()}
                        placeholder="Inserisci il token"
                        className="w-full bg-[#15181C] border border-[#9AA2AD]/30 focus:border-[#E2231A] px-4 py-3 text-sm outline-none mb-3"
                    />
                    <button data-testid="contab-login-btn" onClick={doLogin}
                        className="w-full bg-[#E2231A] hover:bg-[#C11B15] text-white uppercase tracking-widest text-xs py-3">
                        Accedi
                    </button>
                    <p className="text-[10px] text-[#9AA2AD] mt-6 text-center">Usa il token admin condiviso</p>
                </div>
            </div>
        );
    }
    return (
        <div className="min-h-screen bg-[#0B0D10] text-[#F4F2ED]">
            <Nav />
            <div className="max-w-6xl mx-auto px-4 md:px-8 py-6">
                <EventScopeBar token={token} prefix="contab" />
                <ContabilitaDashboard key={eventId} token={token} onLogout={doLogout} />
            </div>
            <Footer />
        </div>
    );
}

/** COMANDO 1 · Ossatura Contabilità — usata da /contabilita e dal tab Admin. */
export function ContabilitaDashboard({ token, onLogout, embedded = false }) {
    const { eventId: activeEventId } = useEventScope();
    const [soggetti, setSoggetti] = useState([]);
    const [operatore, setOperatore] = useState(localStorage.getItem(OP_KEY) || "");
    const [eventi, setEventi] = useState([]);
    const [stripe, setStripe] = useState(null);
    const [riepilogo, setRiepilogo] = useState(null);
    const [loading, setLoading] = useState(false);
    const [cloud, setCloud] = useState(null);
    const [libroRevision, setLibroRevision] = useState(0);

    // form evento
    const [evNome, setEvNome] = useState("");
    const [evData, setEvData] = useState(today());
    const [evBusy, setEvBusy] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [s, e] = await Promise.all([cbSoggetti(token), cbEventi(token)]);
            setSoggetti(s.soggetti || []);
            setEventi((e.eventi || []).filter(event => activeEventId === "tutti" || event._id === activeEventId));
            setLibroRevision(n => n + 1);
            cbStripeRiepilogo(token).then(setStripe).catch(() => {});
            cbRiepilogo(token).then(setRiepilogo).catch(() => {});
            if (!operatore && (s.soggetti || []).length) {
                setOperatore(s.soggetti[0]);
                localStorage.setItem(OP_KEY, s.soggetti[0]);
            }
        } catch (err) {
            if (err?.response?.status === 401) { toast.error("Token non valido"); onLogout && onLogout(); }
            else toast.error("Errore caricamento contabilità");
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); photographerConfig(token).then(setCloud).catch(() => {}); /* eslint-disable-line */ }, [token]);

    const changeOperatore = (v) => { setOperatore(v); localStorage.setItem(OP_KEY, v); };

    const addEvento = async () => {
        if (!evNome.trim()) { toast.error("Nome evento obbligatorio"); return; }
        setEvBusy(true);
        try {
            await cbCreateEvento(token, { nome: evNome.trim(), data: evData });
            setEvNome(""); setEvData(today());
            toast.success("Evento aggiunto");
            await load();
        } catch (err) { toast.error(err?.response?.data?.detail || "Errore evento"); }
        finally { setEvBusy(false); }
    };

    const eventoNome = (id) => (eventi.find(e => e._id === id)?.nome) || "—";

    return (
        <div className="space-y-6" data-testid="contab-dashboard">
            <div className="flex items-center justify-between flex-wrap gap-3">
                <div>
                    <h1 className="font-display text-2xl md:text-3xl uppercase">Contabilità · Movimenti</h1>
                    <p className="text-[11px] text-[#9AA2AD]">Entrate / Uscite · chi ha pagato cosa · allegati</p>
                </div>
                <button onClick={load} disabled={loading} data-testid="contab-reload"
                    className="border border-[#4B7BEA]/40 hover:bg-[#4B7BEA]/10 text-[#4B7BEA] px-3 py-1.5 text-[10px] uppercase tracking-widest disabled:opacity-40">
                    {loading ? "…" : "↻ Ricarica"}
                </button>
            </div>

            {/* Sto operando come */}
            <div className="border border-[#E2231A]/30 bg-[#E2231A]/5 p-3">
                <label className="text-[11px] uppercase tracking-widest text-[#E2231A] font-semibold block mb-2">Sto operando come:</label>
                <div className="flex flex-wrap gap-2" data-testid="contab-operatore">
                    {soggetti.map(s => (
                        <button key={s} onClick={() => changeOperatore(s)} data-testid={`operatore-${s}`}
                            className={`px-3 py-1.5 text-xs uppercase tracking-widest border ${operatore === s
                                ? "border-[#E2231A] bg-[#E2231A] text-white font-semibold"
                                : "border-[#9AA2AD]/30 text-[#9AA2AD] hover:border-[#E2231A]/50"}`}>
                            {s}
                        </button>
                    ))}
                </div>
                <p className="text-[10px] text-[#9AA2AD] mt-2">È il valore di default del campo "Chi ha pagato" nei nuovi movimenti (puoi cambiarlo per singola voce).</p>
            </div>

            {/* Incassi Stripe letti dal CONTATORE GENERALE (stessa fonte della Dashboard) */}
            {stripe && (
                <div className="border border-[#4B7BEA]/30 bg-[#4B7BEA]/5 p-3 md:p-4" data-testid="contab-stripe-panel">
                    <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                        <h2 className="text-sm uppercase tracking-widest text-[#4B7BEA]">📊 Incassi Stripe · evento Battipaglia</h2>
                        <span className="text-[9px] text-[#9AA2AD] uppercase tracking-widest">letto dal contatore generale (area riservata)</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                        <div className="border border-[#9AA2AD]/15 bg-[#0B0D10] p-3" data-testid="stripe-turni">
                            <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD]">Stripe Turni</div>
                            <div className="text-lg font-semibold text-[#16a34a]">{eur(stripe.stripe_turni)}</div>
                        </div>
                        <div className="border border-[#9AA2AD]/15 bg-[#0B0D10] p-3" data-testid="stripe-foto">
                            <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD]">Stripe Foto (lordo)</div>
                            <div className="text-lg font-semibold text-[#16a34a]">{eur(stripe.stripe_foto)}</div>
                        </div>
                        <div className="border border-[#f59e0b]/25 bg-[#f59e0b]/10 p-3" data-testid="contab-cassa-cash">
                            <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD]">💵 Cassa Cash ({stripe.cassa_cash_count || 0})</div>
                            <div className="text-lg font-semibold text-[#f59e0b]">{eur(stripe.cassa_cash)}</div>
                        </div>
                        <div className="border border-[#4B7BEA]/30 bg-[#4B7BEA]/10 p-3" data-testid="stripe-totale">
                            <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD]">Totale Stripe (Entrate)</div>
                            <div className="text-lg font-semibold text-[#4B7BEA]">{eur(stripe.stripe_totale)}</div>
                        </div>
                        <div className="border border-[#E2231A]/30 bg-[#E2231A]/10 p-3" data-testid="stripe-fotografo">
                            <div className="text-[9px] uppercase tracking-widest text-[#9AA2AD]">Fotografo 50% foto (Uscita cash)</div>
                            <div className="text-lg font-semibold text-[#E2231A]">− {eur(stripe.quota_fotografo_foto)}</div>
                        </div>
                    </div>
                    <p className="text-[10px] text-[#9AA2AD] mt-2">
                        Numeri live da Stripe (Cassa Turni + Cassa Foto) + Cassa Cash (paganti in contanti, gestiti dal box in Dashboard). Il fotografo prende il 50% della Cassa Foto, pagato cash → resta {eur(stripe.netto_foto_soci)} ai soci. (Oggi evento unico = Battipaglia; il "Generale" sommerà più eventi in futuro.)
                    </p>
                </div>
            )}

            {/* COMANDO 2 · Riepilogo Entrate/Uscite per evento e per socio */}
            {riepilogo && (
                <div className="grid md:grid-cols-2 gap-4" data-testid="contab-riepilogo">
                    <div className="border border-[#9AA2AD]/15 bg-[#0F1114] p-3 md:p-4">
                        <h2 className="text-sm uppercase tracking-widest text-[#F4F2ED] mb-3">📅 Riepilogo per evento</h2>
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="text-[#9AA2AD] uppercase tracking-widest text-[10px] text-left">
                                    <th className="p-2">Evento</th>
                                    <th className="p-2 text-right">Entrate</th>
                                    <th className="p-2 text-right">Uscite</th>
                                    <th className="p-2 text-right">Saldo</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(riepilogo.per_evento || []).length === 0 ? (
                                    <tr><td colSpan={4} className="p-4 text-center text-[#9AA2AD]">Nessun dato</td></tr>
                                ) : riepilogo.per_evento.map((r) => (
                                    <tr key={r.evento_id} className="border-t border-[#9AA2AD]/10" data-testid={`riep-evento-${r.evento_id}`}>
                                        <td className="p-2 text-[#F4F2ED]">{r.nome}</td>
                                        <td className="p-2 text-right text-[#16a34a]">{eur(r.entrate)}</td>
                                        <td className="p-2 text-right text-[#E2231A]">{eur(r.uscite)}</td>
                                        <td className={`p-2 text-right font-semibold ${r.saldo >= 0 ? "text-[#16a34a]" : "text-[#E2231A]"}`}>{eur(r.saldo)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="border border-[#9AA2AD]/15 bg-[#0F1114] p-3 md:p-4">
                        <h2 className="text-sm uppercase tracking-widest text-[#F4F2ED] mb-3">👤 Riepilogo per socio</h2>
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="text-[#9AA2AD] uppercase tracking-widest text-[10px] text-left">
                                    <th className="p-2">Socio</th>
                                    <th className="p-2 text-right">Entrate</th>
                                    <th className="p-2 text-right">Uscite</th>
                                    <th className="p-2 text-right">Saldo</th>
                                </tr>
                            </thead>
                            <tbody>
                                {(riepilogo.per_socio || []).map((r) => (
                                    <tr key={r.soggetto} className="border-t border-[#9AA2AD]/10" data-testid={`riep-socio-${r.soggetto}`}>
                                        <td className="p-2 text-[#F4F2ED]">{r.soggetto}</td>
                                        <td className="p-2 text-right text-[#16a34a]">{eur(r.entrate)}</td>
                                        <td className="p-2 text-right text-[#E2231A]">{eur(r.uscite)}</td>
                                        <td className={`p-2 text-right font-semibold ${r.saldo >= 0 ? "text-[#16a34a]" : "text-[#E2231A]"}`}>{eur(r.saldo)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                        <p className="text-[9px] text-[#9AA2AD] mt-2">Somma dei movimenti manuali per soggetto (chi ha pagato/incassato).</p>
                    </div>
                </div>
            )}

            <div className="grid md:grid-cols-2 gap-6">
                <EventoForm evNome={evNome} setEvNome={setEvNome} evData={evData} setEvData={setEvData} evBusy={evBusy} addEvento={addEvento} eventi={eventi} />
                <MovimentoForm token={token} soggetti={soggetti} operatore={operatore} eventi={eventi} cloud={cloud} onSaved={load} />
            </div>

            <ContabWorkspace token={token} eventi={eventi} soggetti={soggetti} revision={libroRevision} onChanged={load} />
        </div>
    );
}

function EventoForm({ evNome, setEvNome, evData, setEvData, evBusy, addEvento, eventi }) {
    return (
        <div className="border border-[#9AA2AD]/15 bg-[#0F1114] p-3 md:p-4" data-testid="evento-form">
            <h2 className="text-sm uppercase tracking-widest text-[#F4F2ED] mb-3">🏁 Eventi</h2>
            <div className="space-y-2">
                <input type="text" value={evNome} onChange={e => setEvNome(e.target.value)} placeholder="Nome evento *"
                    data-testid="evento-nome"
                    className="w-full bg-[#0B0D10] border border-[#9AA2AD]/30 focus:border-[#E2231A] px-3 py-2 text-sm outline-none" />
                <div className="flex gap-2">
                    <input type="date" value={evData} onChange={e => setEvData(e.target.value)} data-testid="evento-data"
                        className="flex-1 bg-[#0B0D10] border border-[#9AA2AD]/30 focus:border-[#E2231A] px-3 py-2 text-sm outline-none" />
                    <button onClick={addEvento} disabled={evBusy} data-testid="evento-add"
                        className="border border-[#16a34a]/60 bg-[#16a34a]/10 hover:bg-[#16a34a]/20 text-[#16a34a] px-4 py-2 text-xs uppercase tracking-widest font-semibold disabled:opacity-40">
                        {evBusy ? "…" : "+ Aggiungi"}
                    </button>
                </div>
            </div>
            <div className="mt-3 space-y-1 max-h-40 overflow-auto">
                {eventi.length === 0 && <p className="text-[11px] text-[#9AA2AD]">Nessun evento ancora.</p>}
                {eventi.map(e => (
                    <div key={e._id} className="flex items-center justify-between text-xs border-b border-[#9AA2AD]/10 py-1.5" data-testid={`evento-item-${e._id}`}>
                        <span className="text-[#F4F2ED]" data-testid={`evento-name-${e._id}`}>{eventDisplayName(e)}</span>
                        <span className="text-[#9AA2AD]">{e.data || "—"}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function MovimentoForm({ token, soggetti, operatore, eventi, cloud, onSaved }) {
    const [data, setData] = useState(today());
    const [tipo, setTipo] = useState("Uscita");
    const [descrizione, setDescrizione] = useState("");
    const [fornitore, setFornitore] = useState("");
    const [importo, setImporto] = useState("");
    const [sconto, setSconto] = useState("0");
    const [soggetto, setSoggetto] = useState(operatore || "");
    const [tipoCosto, setTipoCosto] = useState("DirettoEvento");
    const { eventId: activeEventId } = useEventScope();
    const [eventoId, setEventoId] = useState(activeEventId === "tutti" ? "" : activeEventId);
    const [spalma, setSpalma] = useState("1");
    const [allegati, setAllegati] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => { setSoggetto(operatore || ""); }, [operatore]);

    const totaleEff = (parseFloat(importo) || 0) - (parseFloat(sconto) || 0);

    const uploadAllegati = async (files) => {
        if (!files || !files.length) return;
        if (!cloud) { toast.error("Config upload non pronta, riprova tra 1s"); return; }
        setUploading(true);
        const added = [];
        for (const f of Array.from(files)) {
            try {
                const fd = new FormData();
                fd.append("file", f);
                fd.append("upload_preset", cloud.upload_preset);
                fd.append("folder", "velora_contab");
                const res = await fetch(cloud.upload_url, { method: "POST", body: fd });
                if (!res.ok) { toast.error(`${f.name}: upload fallito`); continue; }
                const j = await res.json();
                added.push({ secure_url: j.secure_url, filename: f.name, format: j.format, resource_type: j.resource_type });
            } catch (e) { toast.error(`${f.name}: errore rete`); }
        }
        setAllegati(prev => [...prev, ...added]);
        setUploading(false);
        if (added.length) toast.success(`${added.length} allegato/i caricato/i`);
    };

    const reset = () => {
        setData(today()); setTipo("Uscita"); setDescrizione(""); setFornitore("");
        setImporto(""); setSconto("0"); setSoggetto(operatore || "");
        setTipoCosto("DirettoEvento"); setEventoId(""); setSpalma("1"); setAllegati([]);
    };

    const submit = async () => {
        if (!soggetto) { toast.error("Seleziona il soggetto (chi ha pagato)"); return; }
        if (!(parseFloat(importo) > 0)) { toast.error("Importo non valido"); return; }
        if (tipo === "Uscita" && tipoCosto === "DirettoEvento" && !eventoId) { toast.error("Scegli l'evento (costo diretto evento)"); return; }
        setBusy(true);
        try {
            await cbCreateMovimento(token, {
                data, tipo, descrizione, fornitore,
                importo: parseFloat(importo) || 0,
                sconto: parseFloat(sconto) || 0,
                soggetto,
                tipo_costo: tipo === "Uscita" ? tipoCosto : null,
                evento_id: tipo === "Uscita" && tipoCosto === "DirettoEvento" ? eventoId : null,
                spalma_eventi: tipo === "Uscita" && tipoCosto === "GeneraleInvestimento" ? (parseInt(spalma) || 1) : 1,
                allegati,
                operatore,
            });
            toast.success("Movimento salvato");
            reset();
            onSaved && onSaved();
        } catch (err) { toast.error(err?.response?.data?.detail || "Errore salvataggio"); }
        finally { setBusy(false); }
    };

    const inp = "w-full bg-[#0B0D10] border border-[#9AA2AD]/30 focus:border-[#E2231A] px-3 py-2 text-sm outline-none";

    return (
        <div className="border border-[#9AA2AD]/15 bg-[#0F1114] p-3 md:p-4" data-testid="movimento-form">
            <h2 className="text-sm uppercase tracking-widest text-[#F4F2ED] mb-3">➕ Nuovo movimento</h2>
            <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                    <div>
                        <label className="text-[10px] uppercase tracking-widest text-[#9AA2AD]">Data</label>
                        <input type="date" value={data} onChange={e => setData(e.target.value)} data-testid="mov-data" className={inp} />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-widest text-[#9AA2AD]">Tipo</label>
                        <select value={tipo} onChange={e => setTipo(e.target.value)} data-testid="mov-tipo" className={inp}>
                            <option value="Uscita">Uscita</option>
                            <option value="Entrata">Entrata</option>
                        </select>
                    </div>
                </div>
                <div>
                    <label className="text-[10px] uppercase tracking-widest text-[#9AA2AD]">Chi ha pagato (soggetto)</label>
                    <select value={soggetto} onChange={e => setSoggetto(e.target.value)} data-testid="mov-soggetto" className={inp}>
                        <option value="">— seleziona —</option>
                        {soggetti.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                </div>
                <div>
                    <label className="text-[10px] uppercase tracking-widest text-[#9AA2AD]">Descrizione</label>
                    <input type="text" value={descrizione} onChange={e => setDescrizione(e.target.value)} data-testid="mov-descrizione" className={inp} />
                </div>
                <div>
                    <label className="text-[10px] uppercase tracking-widest text-[#9AA2AD]">Fornitore</label>
                    <input type="text" value={fornitore} onChange={e => setFornitore(e.target.value)} data-testid="mov-fornitore" className={inp} />
                </div>
                <div className="grid grid-cols-3 gap-2">
                    <div>
                        <label className="text-[10px] uppercase tracking-widest text-[#9AA2AD]">Importo €</label>
                        <input type="number" step="0.01" value={importo} onChange={e => setImporto(e.target.value)} data-testid="mov-importo" className={inp} />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-widest text-[#9AA2AD]">Sconto €</label>
                        <input type="number" step="0.01" value={sconto} onChange={e => setSconto(e.target.value)} data-testid="mov-sconto" className={inp} />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-widest text-[#9AA2AD]">Totale</label>
                        <div className="px-3 py-2 text-sm border border-[#9AA2AD]/20 bg-[#15181C] text-[#16a34a] font-semibold" data-testid="mov-totale">{eur(totaleEff)}</div>
                    </div>
                </div>

                {tipo === "Uscita" && (
                    <>
                        <div>
                            <label className="text-[10px] uppercase tracking-widest text-[#9AA2AD]">Tipo di costo *</label>
                            <select value={tipoCosto} onChange={e => setTipoCosto(e.target.value)} data-testid="mov-tipocosto" className={inp}>
                                {TIPI_COSTO.map(t => <option key={t} value={t}>{COSTO_LABEL[t]}</option>)}
                            </select>
                        </div>
                        {tipoCosto === "DirettoEvento" && (
                            <div>
                                <label className="text-[10px] uppercase tracking-widest text-[#9AA2AD]">Evento *</label>
                                <select value={eventoId} onChange={e => setEventoId(e.target.value)} data-testid="mov-evento" className={inp}>
                                    <option value="">— seleziona evento —</option>
                                    {eventi.map(e => <option key={e._id} value={e._id}>{eventDisplayName(e)}</option>)}
                                </select>
                            </div>
                        )}
                        {tipoCosto === "GeneraleInvestimento" && (
                            <div>
                                <label className="text-[10px] uppercase tracking-widest text-[#9AA2AD]">Spalma su N eventi</label>
                                <input type="number" min="1" value={spalma} onChange={e => setSpalma(e.target.value)} data-testid="mov-spalma" className={inp} />
                            </div>
                        )}
                    </>
                )}

                <div>
                    <label className="text-[10px] uppercase tracking-widest text-[#9AA2AD]">Allegati (foto/PDF ricevute)</label>
                    <input type="file" multiple accept="image/*,application/pdf" onChange={e => { uploadAllegati(e.target.files); e.target.value = ""; }}
                        data-testid="mov-allegati" disabled={uploading}
                        className="w-full text-xs text-[#9AA2AD] file:mr-3 file:py-1.5 file:px-3 file:border-0 file:bg-[#E2231A]/20 file:text-[#E2231A] file:text-xs file:uppercase file:tracking-widest" />
                    {uploading && <p className="text-[10px] text-[#f59e0b] mt-1">Carico allegati…</p>}
                    {allegati.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2" data-testid="mov-allegati-preview">
                            {allegati.map((a, i) => (
                                <div key={i} className="relative border border-[#9AA2AD]/20 bg-[#15181C] p-1">
                                    {a.resource_type === "image" || /\.(jpe?g|png|webp|heic|gif)$/i.test(a.filename || "")
                                        ? <img src={a.secure_url} alt={a.filename} className="w-14 h-14 object-cover" />
                                        : <div className="w-14 h-14 flex items-center justify-center text-[9px] text-[#9AA2AD] text-center px-1">📄 PDF</div>}
                                    <button onClick={() => setAllegati(prev => prev.filter((_, j) => j !== i))}
                                        className="absolute -top-2 -right-2 bg-[#E2231A] text-white w-5 h-5 text-xs leading-none">×</button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <button onClick={submit} disabled={busy || uploading} data-testid="mov-submit"
                    className="w-full bg-[#E2231A] hover:bg-[#C11B15] text-white uppercase tracking-widest text-xs py-3 font-semibold disabled:opacity-40 mt-2">
                    {busy ? "Salvo…" : "💾 Salva movimento"}
                </button>
            </div>
        </div>
    );
}

function MovimentoRow({ m, eventoNome, token, onDeleted }) {
    const [del, setDel] = useState(false);
    const isEntrata = m.tipo === "Entrata";
    const remove = async () => {
        if (!window.confirm("Eliminare questo movimento?")) return;
        setDel(true);
        try { await cbDeleteMovimento(token, m._id); toast.success("Eliminato"); onDeleted && onDeleted(); }
        catch (e) { toast.error("Errore eliminazione"); setDel(false); }
    };
    return (
        <div className="border border-[#9AA2AD]/15 bg-[#15181C] p-3" data-testid={`movimento-${m._id}`}>
            <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className={`text-[9px] uppercase tracking-widest px-2 py-0.5 ${isEntrata ? "bg-[#16a34a]/15 text-[#16a34a]" : "bg-[#E2231A]/15 text-[#E2231A]"}`}>{m.tipo}</span>
                        <span className="text-[10px] text-[#9AA2AD]">{m.data}</span>
                        <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 border border-[#4B7BEA]/40 text-[#4B7BEA]">{m.soggetto}</span>
                        {m.tipo_costo && <span className="text-[9px] text-[#9AA2AD]">· {COSTO_LABEL[m.tipo_costo] || m.tipo_costo}</span>}
                        {m.evento_id && <span className="text-[9px] text-[#9AA2AD]">· {eventoNome(m.evento_id)}</span>}
                        {m.tipo_costo === "GeneraleInvestimento" && m.spalma_eventi > 1 && <span className="text-[9px] text-[#9AA2AD]">· spalmato su {m.spalma_eventi}</span>}
                    </div>
                    <div className="text-sm text-[#F4F2ED] mt-1">{m.descrizione || <span className="text-[#9AA2AD] italic">senza descrizione</span>}</div>
                    {m.fornitore && <div className="text-[11px] text-[#9AA2AD]">Fornitore: {m.fornitore}</div>}
                    {(m.allegati || []).length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2">
                            {m.allegati.map((a, i) => (
                                <a key={i} href={a.secure_url} target="_blank" rel="noreferrer" className="border border-[#9AA2AD]/20 bg-[#0B0D10] p-0.5 block" title={a.filename}>
                                    {a.resource_type === "image" || /\.(jpe?g|png|webp|heic|gif)$/i.test(a.filename || "")
                                        ? <img src={a.secure_url} alt={a.filename} className="w-12 h-12 object-cover" />
                                        : <div className="w-12 h-12 flex items-center justify-center text-[9px] text-[#9AA2AD]">📄</div>}
                                </a>
                            ))}
                        </div>
                    )}
                    <div className="text-[9px] text-[#9AA2AD]/70 mt-1">
                        inserito da {m.inserito_da?.soggetto || "—"} · {(m.inserito_da?.at || "").slice(0, 16).replace("T", " ")}
                    </div>
                </div>
                <div className="text-right shrink-0">
                    <div className={`text-base font-semibold ${isEntrata ? "text-[#16a34a]" : "text-[#E2231A]"}`}>{eur(m.totale_eff)}</div>
                    {Number(m.sconto) > 0 && <div className="text-[9px] text-[#9AA2AD]">imp {eur(m.importo)} − sc {eur(m.sconto)}</div>}
                    <button onClick={remove} disabled={del} data-testid={`mov-delete-${m._id}`}
                        className="mt-2 text-[9px] uppercase tracking-widest text-[#9AA2AD] hover:text-[#E2231A] disabled:opacity-40">
                        {del ? "…" : "🗑 Elimina"}
                    </button>
                </div>
            </div>
        </div>
    );
}
